/**
 * @fileoverview Input/output schemas for the style-guardrails-generator-v2 agent.
 *
 * This agent analyzes writing samples and influences to generate
 * guardrails for style guides: tone keywords, recommended phrases,
 * anti-influences, and custom banned phrases.
 */

import { z } from 'zod';
import { TemplateInfluenceProseSchema } from '@/domains/style-guides/schemas';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

// Prose payload enriched with the template's display name and tags, so the
// model knows the literal influence (e.g. "Cormac McCarthy", "Cyberpunk Noir")
// rather than just the anonymous prose description.
const EnrichedInfluenceSchema = TemplateInfluenceProseSchema.extend({
  name: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

const MixingBoardSchema = z.object({
  narrativeDistance: z.number().min(0).max(4).optional(),
  visualSpecificity: z.number().min(0).max(4).optional(),
  syntacticComplexity: z.number().min(0).max(4).optional(),
  vocabularyRegister: z.number().min(0).max(4).optional(),
  descriptionDensity: z.number().min(0).max(4).optional(),
  pacingCadence: z.number().min(0).max(4).optional(),
  expositionIntegration: z.number().min(0).max(4).optional(),
  dialogueTexture: z.number().min(0).max(4).optional(),
  emotionalSaturation: z.number().min(0).max(4).optional(),
  atmosphericWeight: z.number().min(0).max(4).optional(),
});

const ContentControlsSchema = z.object({
  sexualContent: z.number().min(0).max(4).optional(),
  physicalViolence: z.number().min(0).max(4).optional(),
  emotionalViolence: z.number().min(0).max(4).optional(),
  profanity: z.number().min(0).max(4).optional(),
});

const ExistingGuardrailsSchema = z.object({
  toneKeywords: z.array(z.string()).optional(),
  recommendedPhrases: z.array(z.string()).optional(),
  antiInfluences: z.array(z.string()).optional(),
  customBannedPhrases: z.array(z.string()).optional(),
});

export const InputSchema = z.object({
  logId: z.string(),
  model: z.string(),
  customPrompt: z.string(),
  styleGuideName: z.string().optional().describe('The user-supplied name of the style guide being generated (often a strong signal of intent).'),
  sourceText: z.string().optional().describe('A passage of prose to analyze for style patterns. Used by the extract-styleguide flow to feed the imported book text.'),
  genreTemplates: z.array(EnrichedInfluenceSchema.nullable()).optional().describe('Genre influences enriched with name + tags alongside the prose description.'),
  writerTemplates: z.array(EnrichedInfluenceSchema.nullable()).optional().describe('Writer influences enriched with name + tags alongside the prose description.'),
  universeTemplates: z.array(EnrichedInfluenceSchema.nullable()).optional().describe('Universe influences enriched with name + tags alongside the prose description.'),
  mixingBoard: MixingBoardSchema.optional().describe('The 10 mixing-board sliders (0-4) describing the target prose texture.'),
  contentControls: ContentControlsSchema.optional().describe('The 4 content-rating sliders (0-4).'),
  existingGuardrails: ExistingGuardrailsSchema.optional().describe('Guardrail tags the user has already entered. Treat as anchors to preserve or refine, not overwrite.'),
  // Pulled from the surrounding project (when available) so the guardrails
  // reflect this story's world, not generic literary defaults.
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

/**
 * Hook Input type - accepts template IDs that the hook converts to template prose.
 *
 * Note: This is distinct from Input because the hook performs pre-processing
 * to load templates from IDs before calling executeAgent. The hook passes
 * the loaded templates to the agent, not the IDs.
 */
export type TemplateRef = { templateId: string; name?: string; tags?: string[] };

export type HookInput = {
  styleGuideName?: string;
  sourceText?: string;
  genres?: TemplateRef[];
  writers?: TemplateRef[];
  universes?: TemplateRef[];
  mixingBoard?: z.infer<typeof MixingBoardSchema>;
  contentControls?: z.infer<typeof ContentControlsSchema>;
  existingGuardrails?: z.infer<typeof ExistingGuardrailsSchema>;
  storyContext?: unknown;
  loreContext?: unknown;
};

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Output Schema - the generated guardrails for style guides.
 */
export const OutputSchema = z.object({
  toneKeywords: z.array(z.string()).describe('3-7 adjectives that capture the primary tone and mood of the desired style.'),
  recommendedPhrases: z.array(z.string()).describe('8-15 distinctive words or short phrases (2-4 words) characteristic of the style. A "Green List" of vocabulary to embrace.'),
  antiInfluences: z.array(z.string()).describe('3-7 authors, genres, tropes, or styles to actively AVOID emulating.'),
  customBannedPhrases: z.array(z.string()).describe('10-20 specific words, phrases, or patterns that should be avoided. A "Red List" of vocabulary to reject.'),
  explanation: z.string().describe('A brief explanation of the reasoning behind the guardrails choices.'),
});

export type Output = z.infer<typeof OutputSchema>;
